/* ReQaf Build-Your-Box — client behavior.
 *
 * Custom element that owns the interactive state for the mix-and-match
 * bundle builder: bundle size (1 / 2 / 3), selected origins, live price
 * math, and multi-item /cart/add.js submission. All origin data + tier
 * config is emitted server-side into a JSON script tag; no re-fetching.
 *
 * State model:
 *   { size: 1 | 2 | 3, selected: Set<productId> }
 *
 * Initial state:
 *   - No `?seed=` param → size=1, selected={} (customer picks 1 origin
 *     for a single-box purchase, or bumps size to unlock the bundle
 *     discount)
 *   - `?seed=<handle>` present and eligible → size = URL `?size=` or 3,
 *     selected = { that origin } (max-tier upsell nudge from the PDP
 *     cross-sell strip)
 *   - Unknown seed → treated as absent, console.warn
 *
 * Discount math (mirrors the Shopify Function in reqaf-discounts):
 *   - Total = Σ origin.priceCents for id in selected
 *   - Compare = total + size × 100  (visual "$1/box saved" anchor)
 *   - Both compare + save chip render only when bundle is COMPLETE
 *     (selected.size === size) AND size >= 2. Never over-promise at
 *     size=1 or when the bundle is still being assembled.
 */

class ReqafBuildYourBox extends HTMLElement {
  constructor() {
    super();
    this.config = null;
    this.state = { size: 1, selected: new Set() };
    this.originsById = new Map();
    this.originsByHandle = new Map();
  }

  connectedCallback() {
    const configEl = this.querySelector('[data-reqaf-byb-config]');
    if (!configEl) {
      console.warn('[reqaf-byb] config script tag missing — aborting init');
      return;
    }
    try {
      this.config = JSON.parse(configEl.textContent);
    } catch (err) {
      console.warn('[reqaf-byb] failed to parse config JSON', err);
      return;
    }
    if (!Array.isArray(this.config.origins) || this.config.origins.length === 0) {
      console.warn('[reqaf-byb] no eligible origins — check the origins collection + metafields');
      return;
    }

    for (const o of this.config.origins) {
      this.originsById.set(o.id, o);
      this.originsByHandle.set(o.handle, o);
    }

    this.applyUrlSeed();
    this.wireEvents();
    this.swapGalleryImage();
    this.render();
  }

  applyUrlSeed() {
    const params = new URLSearchParams(window.location.search);
    const seedHandle = params.get('seed');
    let seedSize = parseInt(params.get('size'), 10);
    if (![1, 2, 3].includes(seedSize)) seedSize = null;

    // Try to resolve the seed if one was provided, else fall back to the
    // first eligible origin. Guarantees the customer always lands with a
    // bundle in progress — no empty state that requires a click before
    // anything happens. Default size is 3 (max-tier nudge) unless the
    // URL specified otherwise.
    let seedOrigin = null;
    if (seedHandle) {
      seedOrigin = this.originsByHandle.get(seedHandle);
      if (!seedOrigin) {
        console.warn(`[reqaf-byb] unknown seed handle "${seedHandle}" — falling back to first origin`);
      }
    }
    if (!seedOrigin && this.config.origins.length > 0) {
      seedOrigin = this.config.origins[0];
    }

    this.state.size = seedSize || 3;
    if (seedOrigin) {
      this.state.selected.add(seedOrigin.id);
    }
  }

  wireEvents() {
    // Size tiles
    this.querySelectorAll('[data-reqaf-byb-size]').forEach((btn) => {
      btn.addEventListener('click', () => {
        const size = parseInt(btn.dataset.size, 10);
        if (![1, 2, 3].includes(size)) return;
        this.setSize(size);
      });
    });

    // Origin tiles
    this.querySelectorAll('[data-reqaf-byb-origin]').forEach((btn) => {
      btn.addEventListener('click', () => {
        if (btn.disabled) return;
        const id = parseInt(btn.dataset.productId, 10);
        this.toggleOrigin(id);
      });
    });

    // In-page ATC
    const atc = this.querySelector('[data-reqaf-byb-atc]');
    if (atc) atc.addEventListener('click', () => this.submit());

    // Sticky ATC proxies to the in-page ATC — same handler, one submit path.
    const stickyAtc = document.querySelector('[data-reqaf-byb-sticky-atc]');
    if (stickyAtc) stickyAtc.addEventListener('click', () => this.submit());

    // Gallery thumbs — click scrolls the main carousel to the matching
    // slide (identified by data-origin-id). No more img.src swapping;
    // the main is now a horizontal scroll-snap container with one slide
    // per origin, and the scroll listener below syncs the active thumb
    // back when the user manually swipes the main.
    document.querySelectorAll('[data-reqaf-byb-gallery-thumb]').forEach((thumb) => {
      thumb.addEventListener('click', () => {
        const mainContainer = document.querySelector('[data-reqaf-byb-gallery-main]');
        if (!mainContainer) return;
        const originId = thumb.dataset.originId;
        const slide = mainContainer.querySelector(`[data-reqaf-byb-slide][data-origin-id="${originId}"]`);
        if (slide) {
          mainContainer.scrollTo({ left: slide.offsetLeft, behavior: 'smooth' });
        }
        // Immediate visual feedback on the clicked thumb — the scroll-
        // listener below will re-confirm once the main settles.
        document.querySelectorAll('[data-reqaf-byb-gallery-thumb]').forEach((t) => {
          const active = t === thumb;
          t.classList.toggle('is-active', active);
          t.setAttribute('aria-selected', active ? 'true' : 'false');
        });
      });
    });

    // Main carousel scroll listener — when user swipes/scrolls the main
    // image, find the slide closest to the container's center and sync
    // the active thumb to that origin. Debounced with a short timeout so
    // it fires only once the scroll settles (not on every intermediate
    // scroll event).
    const mainContainer = document.querySelector('[data-reqaf-byb-gallery-main]');
    if (mainContainer) {
      let scrollTimer;
      const syncActiveFromScroll = () => {
        const center = mainContainer.scrollLeft + mainContainer.clientWidth / 2;
        const slides = mainContainer.querySelectorAll('[data-reqaf-byb-slide]');
        for (const slide of slides) {
          if (center >= slide.offsetLeft && center < slide.offsetLeft + slide.offsetWidth) {
            const id = parseInt(slide.dataset.originId, 10);
            if (id) this.setActiveThumb(id);
            break;
          }
        }
      };
      mainContainer.addEventListener('scroll', () => {
        clearTimeout(scrollTimer);
        scrollTimer = setTimeout(syncActiveFromScroll, 80);
      }, { passive: true });
    }

    // Thumb carousel prev/next arrows — scroll the thumb container by
    // ~3 thumbs' worth on each click. Uses smooth scrolling (CSS
    // scroll-behavior: smooth on the container). Also disables the
    // arrows at the scroll boundaries so users don't wonder why clicks
    // do nothing.
    const thumbsContainer = document.querySelector('[data-reqaf-byb-thumbs]');
    const prevBtn = document.querySelector('[data-reqaf-byb-thumbs-prev]');
    const nextBtn = document.querySelector('[data-reqaf-byb-thumbs-next]');

    if (thumbsContainer && prevBtn && nextBtn) {
      const stepSize = () => {
        const first = thumbsContainer.querySelector('[data-reqaf-byb-gallery-thumb]');
        // Approximate step = width of a single thumb + gap. Falls back to
        // 1/3 of the container if no thumbs found (shouldn't happen).
        return first ? first.offsetWidth + 8 : thumbsContainer.clientWidth / 3;
      };
      const updateNavState = () => {
        const atStart = thumbsContainer.scrollLeft <= 1;
        const atEnd = thumbsContainer.scrollLeft + thumbsContainer.clientWidth >= thumbsContainer.scrollWidth - 1;
        prevBtn.disabled = atStart;
        nextBtn.disabled = atEnd;
      };
      prevBtn.addEventListener('click', () => {
        thumbsContainer.scrollBy({ left: -stepSize() * 3, behavior: 'smooth' });
      });
      nextBtn.addEventListener('click', () => {
        thumbsContainer.scrollBy({ left: stepSize() * 3, behavior: 'smooth' });
      });
      thumbsContainer.addEventListener('scroll', updateNavState, { passive: true });
      // Initial state — call after layout has settled (rAF ensures
      // scrollWidth is measured after images have reserved their space).
      requestAnimationFrame(updateNavState);
    }
  }

  setSize(size) {
    if (size === this.state.size) return;

    // Shrinking below current selection count → drop trailing entries.
    if (size < this.state.selected.size) {
      const kept = Array.from(this.state.selected).slice(0, size);
      this.state.selected = new Set(kept);
      this.swapGalleryImage();
    }
    this.state.size = size;
    this.render();
  }

  toggleOrigin(id) {
    const origin = this.originsById.get(id);
    if (!origin || !origin.available) return;

    if (this.state.selected.has(id)) {
      this.state.selected.delete(id);
    } else if (this.state.selected.size >= this.state.size) {
      // At cap. For size=1 (single-origin mode), replace by swapping the
      // current pick for the newly-clicked one — natural "single-select"
      // behavior. For size≥2, keep the cap firm; user must explicitly
      // deselect before picking a replacement (avoids accidental swaps
      // when they already spent thought choosing).
      if (this.state.size === 1) {
        this.state.selected.clear();
        this.state.selected.add(id);
      } else {
        return;
      }
    } else {
      this.state.selected.add(id);
    }

    this.swapGalleryImage();
    this.render();
  }

  swapGalleryImage() {
    // Scroll the main gallery carousel to the slide matching the most-
    // recently selected origin (or the first slide when nothing is
    // selected). Also syncs the thumb strip.
    const mainContainer = document.querySelector('[data-reqaf-byb-gallery-main]');
    if (!mainContainer) return;

    const selectedIds = Array.from(this.state.selected);
    let targetSlide;
    let targetThumbId = null;

    if (selectedIds.length === 0) {
      targetSlide = mainContainer.querySelector('[data-reqaf-byb-slide]');
      // null tells setActiveThumb to fall back to the first thumb
    } else {
      const lastId = selectedIds[selectedIds.length - 1];
      targetSlide = mainContainer.querySelector(`[data-reqaf-byb-slide][data-origin-id="${lastId}"]`);
      targetThumbId = lastId;
    }

    if (targetSlide) {
      mainContainer.scrollTo({ left: targetSlide.offsetLeft, behavior: 'smooth' });
    }
    this.setActiveThumb(targetThumbId);
  }

  setActiveThumb(originId) {
    const thumbs = document.querySelectorAll('[data-reqaf-byb-gallery-thumb]');
    if (thumbs.length === 0) return;

    // If no origin passed (deselection), fall back to the first rendered
    // thumb — matches the server-side default that main image also uses.
    let targetId = originId;
    if (targetId === null) {
      targetId = parseInt(thumbs[0].dataset.originId, 10);
    }

    let activeEl = null;
    thumbs.forEach((thumb) => {
      const thumbId = parseInt(thumb.dataset.originId, 10);
      const active = thumbId === targetId;
      thumb.classList.toggle('is-active', active);
      thumb.setAttribute('aria-selected', active ? 'true' : 'false');
      if (active) activeEl = thumb;
    });
    // Scroll the thumb strip horizontally so the active thumb comes into
    // view. Uses manual scrollLeft on the CONTAINER (not scrollIntoView
    // on the thumb) — scrollIntoView with block: 'nearest' still causes
    // vertical page scroll when the strip itself is off-viewport (e.g.
    // the customer clicks an origin tile after scrolling below the
    // gallery — page would jump up to show the thumb). Manual scrollLeft
    // stays purely horizontal.
    if (activeEl) {
      const thumbsContainer = document.querySelector('[data-reqaf-byb-thumbs]');
      if (thumbsContainer) {
        const containerRect = thumbsContainer.getBoundingClientRect();
        const thumbRect = activeEl.getBoundingClientRect();
        // If thumb is out of container's horizontal view, center it
        if (thumbRect.left < containerRect.left || thumbRect.right > containerRect.right) {
          const thumbLeftInContainer = thumbRect.left - containerRect.left + thumbsContainer.scrollLeft;
          const targetLeft = thumbLeftInContainer - thumbsContainer.clientWidth / 2 + activeEl.offsetWidth / 2;
          thumbsContainer.scrollTo({ left: Math.max(0, targetLeft), behavior: 'smooth' });
        }
      }
    }
  }

  computeTotals() {
    // Sum of individual origin prices — this is what the customer would
    // pay if they bought each box separately. Used as the compare-at
    // anchor when a bundle discount applies.
    let sumCents = 0;
    for (const id of this.state.selected) {
      const o = this.originsById.get(id);
      if (o) sumCents += o.priceCents;
    }

    const complete = this.state.selected.size === this.state.size;
    const discountEligible = complete && this.state.size >= 2;

    // Look up the exact discount amount from the tiers config — mirrors
    // the Shopify Function's math so the storefront preview matches
    // what actually gets applied at checkout.
    let discountCents = 0;
    if (discountEligible) {
      const tier = (this.config.tiers || []).find((t) => t.distinct === this.state.size);
      if (tier && typeof tier.off_cents === 'number') {
        discountCents = tier.off_cents;
      }
    }

    // Total = what the customer actually pays (post-discount).
    // Compare = sum of individual prices (pre-discount anchor).
    // Save = the discount amount itself.
    const totalCents = sumCents - discountCents;
    const compareCents = discountEligible ? sumCents : null;
    const saveCents = discountCents;

    return { totalCents, compareCents, saveCents, complete, discountEligible };
  }

  render() {
    const { size, selected } = this.state;
    const totals = this.computeTotals();

    // Size tiles
    this.querySelectorAll('[data-reqaf-byb-size]').forEach((btn) => {
      const active = parseInt(btn.dataset.size, 10) === size;
      btn.classList.toggle('is-selected', active);
      btn.setAttribute('aria-checked', active ? 'true' : 'false');
    });

    // Origin tiles
    this.querySelectorAll('[data-reqaf-byb-origin]').forEach((btn) => {
      const id = parseInt(btn.dataset.productId, 10);
      const isSelected = selected.has(id);
      // In size=1 (single-select) mode, keep all tiles clickable so the
      // customer can freely swap. Cap-based disabling only makes sense
      // for size≥2 where the customer is building up a multi-origin
      // bundle and we want to protect their in-progress selections.
      const atCap = size >= 2 && selected.size >= size && !isSelected;
      btn.classList.toggle('is-selected', isSelected);
      const origin = this.originsById.get(id);
      const shouldDisable = (origin && !origin.available) || atCap;
      btn.disabled = shouldDisable;
    });

    // Origin count
    const countEl = this.querySelector('[data-reqaf-byb-count]');
    if (countEl) {
      countEl.textContent = `${selected.size} / ${size}`;
      countEl.classList.toggle('is-complete', totals.complete);
    }

    // Price row
    const priceEl = this.querySelector('[data-reqaf-byb-price]');
    const compareEl = this.querySelector('[data-reqaf-byb-compare]');
    const saveEl = this.querySelector('[data-reqaf-byb-save]');
    const metaEl = this.querySelector('[data-reqaf-byb-meta]');

    if (priceEl) {
      priceEl.textContent = selected.size > 0 ? this.formatMoney(totals.totalCents) : '—';
    }
    if (compareEl) {
      if (totals.discountEligible) {
        compareEl.textContent = this.formatMoney(totals.compareCents);
        compareEl.removeAttribute('hidden');
      } else {
        compareEl.setAttribute('hidden', '');
      }
    }
    if (saveEl) {
      if (totals.discountEligible) {
        saveEl.textContent = `Save $${(totals.saveCents / 100).toFixed(0)}`;
        saveEl.removeAttribute('hidden');
      } else {
        saveEl.setAttribute('hidden', '');
      }
    }
    if (metaEl) metaEl.textContent = this.metaText();

    // ATC (in-page + sticky mirror). Label stays simple — "Add to cart"
    // when complete, "Pick N more origin" when not. Box count is already
    // visible on the selected Step 1 tile + meta line, so repeating it
    // in the button label was redundant.
    const remaining = size - selected.size;
    const label = totals.complete
      ? 'Add to cart'
      : `Pick ${remaining} more origin${remaining === 1 ? '' : 's'}`;
    const totalStr = selected.size > 0 ? this.formatMoney(totals.totalCents) : '';

    const compareStr = totals.discountEligible ? this.formatMoney(totals.compareCents) : '';

    const inPage = this.querySelector('[data-reqaf-byb-atc]');
    const inPageLabel = this.querySelector('[data-reqaf-byb-atc-label]');
    const inPageTotal = this.querySelector('[data-reqaf-byb-atc-total]');
    const inPageCompare = this.querySelector('[data-reqaf-byb-atc-compare]');
    if (inPage) inPage.disabled = !totals.complete;
    if (inPageLabel) inPageLabel.textContent = label;
    if (inPageTotal) inPageTotal.textContent = totalStr;
    if (inPageCompare) {
      if (compareStr) {
        inPageCompare.textContent = compareStr;
        inPageCompare.removeAttribute('hidden');
      } else {
        inPageCompare.setAttribute('hidden', '');
      }
    }

    const sticky = document.querySelector('[data-reqaf-byb-sticky-atc]');
    const stickyLabel = document.querySelector('[data-reqaf-byb-sticky-label]');
    const stickyTotal = document.querySelector('[data-reqaf-byb-sticky-total]');
    const stickyCompare = document.querySelector('[data-reqaf-byb-sticky-compare]');
    if (sticky) sticky.disabled = !totals.complete;
    if (stickyLabel) stickyLabel.textContent = label;
    if (stickyTotal) stickyTotal.textContent = totalStr;
    if (stickyCompare) {
      if (compareStr) {
        stickyCompare.textContent = compareStr;
        stickyCompare.removeAttribute('hidden');
      } else {
        stickyCompare.setAttribute('hidden', '');
      }
    }
  }

  metaText() {
    const { size, selected } = this.state;
    const totals = this.computeTotals();
    const saveAmt = size === 2 ? this.tierSave(2) : size === 3 ? this.tierSave(3) : 0;
    const remaining = size - selected.size;

    if (selected.size === 0) {
      if (size === 1) return 'Pick 1 origin to begin.';
      return `Pick ${size} origins${saveAmt ? ` — save $${saveAmt}` : ''}.`;
    }
    if (!totals.complete) {
      return `Pick ${remaining} more origin${remaining === 1 ? '' : 's'}${saveAmt ? ` — save $${saveAmt}` : ''}.`;
    }
    if (size === 1) return `${selected.size * this.config.unit_sub_qty} drip bags · 1 box`;
    return `${size * this.config.unit_sub_qty} drip bags · ${size} boxes · save $${saveAmt}`;
  }

  tierSave(distinct) {
    const tier = (this.config.tiers || []).find((t) => t.distinct === distinct);
    return tier ? Math.round(tier.off_cents / 100) : 0;
  }

  formatMoney(cents) {
    // Match Shopify's default money format (no locale i18n here — the
    // theme's `money_format` is `${{amount}}` for USD).
    return `$${(cents / 100).toFixed(2)}`;
  }

  async submit() {
    const totals = this.computeTotals();
    if (!totals.complete) return;

    const errorEl = this.querySelector('[data-reqaf-byb-error]');
    if (errorEl) {
      errorEl.classList.remove('is-visible');
      errorEl.textContent = '';
    }

    // Unique-per-submission ID so the Function can treat each "click Add"
    // as its own bundle instance. Two submissions with the same 3 origins
    // → two distinct bundle IDs → tier discount applies twice (customer
    // gets $6 instead of $3). Without this, Shopify auto-merges same
    // variant + same properties into a single line with quantity=2 and
    // the Function can't tell "1 bundle bought twice" from "1 bundle."
    // Different bundle IDs = different property values = different lines.
    const bundleId = (globalThis.crypto?.randomUUID?.() || `${Date.now()}-${Math.random().toString(36).slice(2)}`);

    const items = Array.from(this.state.selected).map((id) => {
      const o = this.originsById.get(id);
      // `_reqaf_bundle` = boolean flag (quantity-breaks reads this to skip
      // BYB-tagged lines and avoid double-discounting).
      // `_reqaf_bundle_id` = unique per-submission grouping key (mix-match
      // reads this to count bundles as instances, not as a flat cart set).
      // Underscore prefix hides both from cart line display.
      return {
        id: o.variantId,
        quantity: 1,
        properties: {
          "_reqaf_bundle": "true",
          "_reqaf_bundle_id": bundleId,
        },
      };
    });

    const cartDrawer = document.querySelector('cart-drawer');
    const sections = cartDrawer
      ? cartDrawer.getSectionsToRender().map((s) => s.id).join(',')
      : 'cart-drawer,cart-icon-bubble';

    this.setLoading(true);
    try {
      const response = await fetch(`${window.Shopify?.routes?.root || '/'}cart/add.js`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
          'X-Requested-With': 'XMLHttpRequest',
        },
        body: JSON.stringify({
          items,
          sections,
          sections_url: window.location.pathname,
        }),
      });
      const json = await response.json();

      if (!response.ok || json.status) {
        this.showError(json.description || json.message || 'Sorry, we could not add these to your cart.');
        return;
      }

      if (cartDrawer && typeof cartDrawer.renderContents === 'function') {
        // Dawn's renderContents only clears `is-empty` off `.drawer__inner`,
        // but the ReQaf drawer template stamps it on the outer <cart-drawer>
        // element too (line 117 of reqaf-cart-drawer.liquid). CSS hides the
        // footer/checkout while `is-empty` sits on the wrapper. Strip it here
        // — matches what product-form.js does after a successful add.
        if (cartDrawer.classList.contains('is-empty')) {
          cartDrawer.classList.remove('is-empty');
        }
        cartDrawer.renderContents(json);
      }
      if (typeof window.publish === 'function' && window.PUB_SUB_EVENTS?.cartUpdate) {
        window.publish(window.PUB_SUB_EVENTS.cartUpdate, {
          source: 'reqaf-build-your-box',
          cartData: json,
        });
      }
    } catch (err) {
      console.error('[reqaf-byb] add-to-cart failed', err);
      this.showError('Network error — please try again.');
    } finally {
      this.setLoading(false);
    }
  }

  setLoading(isLoading) {
    const inPage = this.querySelector('[data-reqaf-byb-atc]');
    const sticky = document.querySelector('[data-reqaf-byb-sticky-atc]');
    [inPage, sticky].forEach((el) => {
      if (!el) return;
      el.classList.toggle('loading', isLoading);
      if (isLoading) el.setAttribute('aria-busy', 'true');
      else el.removeAttribute('aria-busy');
    });
  }

  showError(msg) {
    const errorEl = this.querySelector('[data-reqaf-byb-error]');
    if (!errorEl) return;
    errorEl.textContent = msg;
    errorEl.classList.add('is-visible');
  }
}

if (!customElements.get('reqaf-build-your-box')) {
  customElements.define('reqaf-build-your-box', ReqafBuildYourBox);
}
